import React, { useActionState, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/button/Button.tsx";
import Input from "../../components/form-item/Input.tsx";
import IconButton from "../../components/button/IconButton.tsx";
import ToolTip from "../../components/tooltip/ToolTip.tsx";
import SvgLoadingAnimation from "../../components/loading/index.tsx";
import { useRequestPasswordResetMutation } from "../../app/services/passwordReset";
import Modal from "../../components/modal/Modal";
import { AuthErrorMessage } from "../../components/layouts/authStyles";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormState = { emailError: string; generalError: string };

const PasswordResetRequest: React.FC = () => {
  const navigate = useNavigate();
  const [requestReset] = useRequestPasswordResetMutation();
  const [email, setEmail] = useState("");

  const isValidEmail = EMAIL_REGEX.test(email.trim());

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.currentTarget.requestSubmit();
    }
  };

  const [state, formAction, isPending] = useActionState(
    async (_prevState: FormState, formData: FormData) => {
      const emailValue = (formData.get("email") as string).trim();

      if (!emailValue) {
        return { emailError: "メールアドレスを入力してください。", generalError: "" };
      }
      if (!EMAIL_REGEX.test(emailValue)) {
        return { emailError: "有効なメールアドレスを入力してください。", generalError: "" };
      }

      try {
        const result = await requestReset({ email: emailValue });

        if (result.data) {
          navigate("/password-reset/sent");
          return { emailError: "", generalError: "" };
        }

        if (result.error) {
          const errorData = (result.error as any)?.data;
          if (errorData?.errors?.email) {
            return { emailError: errorData.errors.email[0], generalError: "" };
          }
          return { emailError: "", generalError: "送信に失敗しました。再度お試しください。" };
        }
      } catch {
        return {
          emailError: "",
          generalError: "ネットワークエラーが発生しました。インターネット接続を確認してください。",
        };
      }

      return { emailError: "", generalError: "" };
    },
    { emailError: "", generalError: "" },
  );

  return (
    <>
      <Modal variant="auth">
        <div className="u-col u-gap32">
          <ToolTip description="サインインに戻る" placement="right">
            <IconButton onClick={() => navigate("/signin")} icon="chevron_left" />
          </ToolTip>

          <form action={formAction} onKeyDown={handleKeyDown}>
            <div className="u-col u-gap32">
              {state?.generalError && (
                <AuthErrorMessage>{state.generalError}</AuthErrorMessage>
              )}

              <div className="u-w100 u-col u-gap16">
                <Input
                  label=""
                  type="email"
                  placeholder="ご登録済みのメールアドレス"
                  name="email"
                  option="required"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={state?.emailError}
                />
              </div>
              <Button
                type="submit"
                theme="primary"
                variant="filled"
                size="large"
                width="full"
                rightIcon="chevron_right"
                disabled={!isValidEmail || isPending}
              >
                {isPending ? "送信中..." : "リセットリンクを送信"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      {isPending && <SvgLoadingAnimation />}
    </>
  );
};

export default PasswordResetRequest;
